import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { GivelifyButton } from '../common/Button/GivelifyButton';
import { colors } from '../../../theme';
import Modal from '../common/Modal';
import { ErrorInfoIcon } from '../../../icons';
import { Text } from '../common/Text/Text';
import { GivingApiResponse } from '../../../../utils/makeApiRequest';
import { useApiRequest } from '../../../../hooks/useGivingApiRequest';
import { FailedDonationResponse, getFailedDonationRequest } from './api';
import { HeartLoading } from '../../../HeartLoading';
import { removeQueryString } from '../../../../hooks/useQueryString';

const extractDate = (date?: string) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export interface EnvelopeWithAmount {
  amount: number;
  envelopeId: number;
}

interface RetryDonationProps {
  retryDonationId: string | undefined;
  handleFailedDonation: (
    envelopes: EnvelopeWithAmount[],
    callback: () => unknown
  ) => unknown;
  setRetryDonationId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const StyledDiv = styled.div`
  padding: 40px;
  .error-info-icon {
    padding: 12px;
    margin-top: 24px;
    margin-bottom: 24px;
    border-radius: 100px;
    display: inline-block;
    background-color: ${colors.utilityErrorLightColor};
    svg {
      margin-bottom: -3px;
    }
  }
  .retry-donation-heading {
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0.2px;
  }
  .modal-subheading {
    max-width: 295px;
    margin: 16px auto 40px auto;
  }
  .donations-wrapper {
    gap: 24px;
    display: flex;
    margin-top: 16px;
    padding: 20px 16px;
    align-items: center;
    border-radius: 20px;
    justify-content: space-between;
    border: 1px solid ${colors.utilitySeparatorColor};
    .donation-details {
      text-align: left;
      .donation-status {
        font-size: 14px;
        color: ${colors.utilityError};
      }
      .donation-time {
        font-size: 14px;
        color: ${colors.primarySecondaryContent};
      }
    }
  }
  .submit-button {
    margin-top: 24px;
  }
  .dismiss-button {
    padding: 14px;
    margin-top: 8px;
  }
`;

export const retryDonationQueryString = 'retryDonationId';
const RetryDonation: React.FC<RetryDonationProps> = ({
  retryDonationId,
  setRetryDonationId,
  handleFailedDonation
}) => {
  const [requestState, makeRequest] =
    useApiRequest<GivingApiResponse<FailedDonationResponse>>();

  useEffect(() => {
    if (!retryDonationId || requestState.type === 'REQUEST_START') return;
    makeRequest(getFailedDonationRequest(retryDonationId));
  }, [retryDonationId]);

  const handleClose = useCallback(() => {
    setRetryDonationId(undefined);
    removeQueryString(retryDonationQueryString);
  }, [setRetryDonationId]);

  if (!retryDonationId) return null;

  const response = requestState.data?.response.payload;
  if (
    requestState.type === 'REQUEST_ERROR' ||
    (requestState.type === 'REQUEST_SUCCESS' && !response)
  ) {
    handleClose();
    return null;
  }

  const handleFailedDonationClick = () => {
    if (!response) return;
    const envelopes = response.envelopes;
    const e: EnvelopeWithAmount[] | undefined = envelopes?.map((x) => ({
      amount: x.amount,
      envelopeId: x.id
    }));
    e && handleFailedDonation(e, handleClose);
  };

  const isLoading =
    requestState.type === 'REQUEST_INIT' ||
    requestState.type == 'REQUEST_START';
  return (
    <Modal
      active={true}
      onClose={handleClose}
      className="login-modal"
      shouldCloseOnOverlayClick={false}
    >
      {isLoading ? (
        <HeartLoading />
      ) : (
        <StyledDiv>
          <div className="error-info-icon">
            <ErrorInfoIcon />
          </div>
          <Text
            weight="extraBold"
            className="retry-donation-heading"
          >
            Your recent gift
            <br />
            didn’t go through
          </Text>
          <div className="donations-wrapper">
            <div className="donation-details">
              <Text>{response?.donee.name || 'Organization'}</Text>
              <Text>
                <span className="donation-status">Failed</span>{' '}
                <span className="donation-time">
                  - {extractDate(response?.gift.date)}
                </span>
              </Text>
            </div>
            <div className="donation-amount">
              <Text
                variant="medium"
                weight="extraBold"
              >
                ${response?.gift.amount}
              </Text>
            </div>
          </div>
          <GivelifyButton
            onClick={handleFailedDonationClick}
            className="submit-button"
          >
            Try Gift Again
          </GivelifyButton>
          <Text
            color="primarySecondaryContent"
            onClick={handleClose}
            className="dismiss-button"
          >
            Dismiss
          </Text>
        </StyledDiv>
      )}
    </Modal>
  );
};

export default RetryDonation;
